import React, { useCallback, useEffect, useState } from "react";
import { formatAmount, formatAmountFree, parseValue } from "../../../lib/numbers";
import { BASIS_POINTS_DIVISOR } from "../../../lib/legacy";
import { ethers } from "ethers";
import RewardRouter from "../../../abis/RewardRouter.json";
import { callContract } from "../../../lib/contracts";
import ExternalLink from "../../ExternalLink/ExternalLink";
import { t, Trans } from "@lingui/macro";
import classes from "./UnstakeModalStyles.module.scss";
import classNames from "classnames";
import { ActionButton } from "../../Buttons/ActionButton/ActionButton";
import { ModalBase } from "../ModalBase/ModalBase";
import { useWeb3React } from "@web3-react/core";
import { useChainId } from "../../../lib/chains";
import { UNSTAKING_WILL_BURN } from "../../../constants/links";

type Props = {
  onClose: () => void;
  rewardRouterAddress: string;
  setPendingTxns: any;
  multiplierPointsAmount: any;
  bonusCadInFeeCad: any;
  unstakeMethodName: string;
  title: string;
  maxAmount: string;
  reservedAmount: any;
  unstakingTokenSymbol: string;
};

const SHOULD_SHOW_REDUCTING_AMOUNT = true;

export const UnstakeModal = ({
  onClose,
  rewardRouterAddress,
  setPendingTxns,
  multiplierPointsAmount,
  bonusCadInFeeCad,
  unstakeMethodName,
  title,
  maxAmount,
  reservedAmount,
  unstakingTokenSymbol,
}: Props) => {
  const [isUnstaking, setIsUnstaking] = useState(false);
  const [value, setValue] = useState("");
  const { library } = useWeb3React();
  const { chainId } = useChainId();
  const [amount, setAmount] = useState<ethers.BigNumber | undefined>();
  const [burnAmount, setBurnAmount] = useState<ethers.BigNumber | undefined>();
  const [rewardReductionBasisPoints, setRewardReductionBasisPoints] = useState<ethers.BigNumber | undefined>();

  useEffect(() => {
    setAmount(parseValue(value, 18));
  }, [value]);

  useEffect(() => {
    if (
      multiplierPointsAmount &&
      multiplierPointsAmount.gt(0) &&
      amount &&
      amount.gt(0) &&
      bonusCadInFeeCad &&
      bonusCadInFeeCad.gt(0)
    ) {
      setBurnAmount(multiplierPointsAmount.mul(amount).div(bonusCadInFeeCad));
    }
  }, [multiplierPointsAmount, amount, bonusCadInFeeCad]);

  useEffect(() => {
    if (burnAmount && bonusCadInFeeCad) {
      setRewardReductionBasisPoints(burnAmount.mul(BASIS_POINTS_DIVISOR).div(bonusCadInFeeCad));
    }
  }, [burnAmount, bonusCadInFeeCad]);

  const getError = useCallback(() => {
    if (!amount) {
      return t`Enter an amount`;
    }
    if (maxAmount && amount.gt(maxAmount)) {
      return t`Max amount exceeded`;
    }
  }, [maxAmount, amount]);

  const onClickPrimary = () => {
    setIsUnstaking(true);
    const contract = new ethers.Contract(rewardRouterAddress, RewardRouter.abi, library.getSigner());
    callContract(chainId, contract, unstakeMethodName, [amount], {
      sentMsg: t`Unstake submitted!`,
      failMsg: t`Unstake failed.`,
      successMsg: t`Unstake completed!`,
      setPendingTxns,
    })
      .then(async (res) => {
        onClose();
      })
      .finally(() => {
        setIsUnstaking(false);
      });
  };

  const isPrimaryEnabled = () => {
    const error = getError();
    if (error) {
      return false;
    }
    if (isUnstaking) {
      return false;
    }
    return true;
  };

  const getPrimaryText = () => {
    const error = getError();
    if (error) {
      return error;
    }
    if (isUnstaking) {
      return t`Unstaking...`;
    }
    return t`Unstake`;
  };

  return (
    <ModalBase title={title} onClose={onClose}>
      <div className={classes.modalWrapper}>
        <div className={classes.inputWrapper}>
          <div className={classes.content}>
            <span className={classes.title}>
              <Trans>Stake</Trans>
            </span>
            <span
              className={classNames(classes.title, classes.titleClickable)}
              onClick={() => setValue(formatAmountFree(maxAmount, 18, 18))}
            >
              <Trans>Max</Trans>: {formatAmount(maxAmount, 18, 4, true)}
            </span>
          </div>
          <div className={classes.content}>
            <input
              type="number"
              placeholder="0.0"
              className={classes.input}
              value={value}
              onChange={(e) => setValue(e.target.value)}
            />
            <span className={classes.symbolTitle}>{unstakingTokenSymbol}</span>
          </div>
        </div>

        {reservedAmount && reservedAmount.gt(0) && (
          <div className={classes.note}>
            You have {formatAmount(reservedAmount, 18, 2, true)} tokens reserved for vesting.
          </div>
        )}

        {burnAmount && burnAmount.gt(0) && rewardReductionBasisPoints && rewardReductionBasisPoints.gt(0) && (
          <div className={classes.note}>
            <Trans>Unstaking will burn</Trans>&nbsp;
            <ExternalLink className={classes.link} href={UNSTAKING_WILL_BURN}>
              {formatAmount(burnAmount, 18, 4, true)} <Trans>Multiplier Points</Trans>
            </ExternalLink>
            .&nbsp;
            {SHOULD_SHOW_REDUCTING_AMOUNT && (
              <span>
                <Trans>Boost Percentage</Trans>: -{formatAmount(rewardReductionBasisPoints, 2, 2)}%.
              </span>
            )}
          </div>
        )}

        <ActionButton
          onClick={onClickPrimary}
          type={"outlined"}
          buttonText={getPrimaryText()}
          disabled={!isPrimaryEnabled()}
          className={classes.button}
        />
      </div>
    </ModalBase>
  );
};
